import React, { useEffect, useCallback, useState, useRef } from "react";
import { array, object } from "prop-types";
import get from "lodash/get";
import { useSelector } from "react-redux";
import { useInView } from "react-intersection-observer";
import { StoryElementCard } from "../../../arrow/components/Molecules/StoryElementCard";
import { StoryTags } from "../../../arrow/components/Atoms/StoryTags";
import { Paywall } from "../../../arrow/components/Rows/Paywall";
import { LatestEditionMagazineBlock } from "../../molecules/LatestEditionMagazineBlock";
import MetypeCommentsWidgetWrapper from "./metype-comments";
import AdComponent from "../../molecules/AdComponent";
import MoreAboutAuthors from "../../../arrow/components/Atoms/MoreAboutAuthors";
import { useDeviceType } from "../../customHooks/useDeviceType";
import { useSummaryCheck } from "../../customHooks/useSummaryCheck";
import { getCategoryAndSubCategory, getTemplateName } from "../../../ga-utils";
import "./story-elements.m.css";
import FaqCard from "../../molecules/FaqCard";
import SummaryCard from "../../molecules/SummaryCard";
import { AppLogo } from "../../atoms";

const RenderCards = ({ story, cards, recommendedStories, taboola, isCreatorStory }) => {
  const qtState = useSelector((state) => state);
  const member = useSelector((state) => get(state, ["member"], null));
  const isMagazineStory = story["story-template"] === "magazine";
  const isTexteStory = story["story-template"] === "text";
  const qtConfig = get(qtState, ["qt", "config"], {});
  const isSponsoredStory = get(story, ["story-template"], "") === "sponsored";
  const isStoryBehindPaywall = !member && isMagazineStory;
  const isDesktop = useDeviceType() === "desktop";
  const isAdfreePage = get(qtState, ["qt", "config", "subscriber"]) === "true";
  const isWebView = get(qtState, ["qt", "config", "isWebView"]) === true;
  const storyHeadline = get(story, ["headline"], "");
  const storyMetadata = get(story, ["metadata"], "");
  const faqUrl = get(qtConfig, ["publisher-attributes", "publisher-faq-url"], false);
  const [isAiOpen, setIsAiOpen] = useState(true);
  const [faqData, setFaqData] = useState([]);
  const targetRef = useRef(null);
  const [isTop, setIsTop] = useState(false);
  const [isFaqInViewport, setIsFaqInViewport] = useState(false);
  const storyId = get(story, ["id"], "") || get(story, ["story-content-id"], "");
  const hasFaqData = faqData.length > 0;
  const [isfaqPopup, setIsfaqPopup] = useState(false);

  useEffect(() => {
    if (!isTexteStory || !storyId || faqUrl === false) {
      setFaqData([]);
      return;
    }

    const abortController = new AbortController();

    const fetchFaqData = async () => {
      try {
        const response = await fetch(
          `${faqUrl}/${encodeURIComponent(storyId)}`,
          { signal: abortController.signal }
        );
        if (!response.ok) {
          throw new Error(`FAQ API failed with status ${response.status}`);
        }

        const data = await response.json();
        const hasFaqs = get(data, ["hasFaqs"], false);

        if (!hasFaqs) {
          setFaqData([]);
          return;
        }

        const apiFaqs = get(data, ["faqs"], []);
        const formattedFaqs = apiFaqs
          .map((faq) => ({
            title: get(faq, ["question"], ""),
            content: get(faq, ["answer"], ""),
          }))
          .filter((faq) => faq.title && faq.content);

        setFaqData(formattedFaqs);
      } catch (error) {
        if (error.name !== "AbortError") {
          setFaqData([]);
          console.error("Failed to fetch FAQs:", error);
        }
      }
    };

    fetchFaqData();

    return () => {
      abortController.abort();
    };
  }, [isTexteStory, storyId, faqUrl]);
  useEffect(() => {
    const handleScroll = () => {
      if (targetRef.current) {
        const top = targetRef.current.getBoundingClientRect().top;
        const isMobile = window.innerWidth <= 768;
        const offset = isMobile ? 570 : 0;

        if (top <= offset) {
          setIsTop(true);
        } else {
          setIsTop(false);
        }
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  const handleAiClick = () => {
    setIsAiOpen(!isAiOpen);
  };

  const { normalCards, creatorYoutubeCards } = cards.reduce(
    (acc, card) => {
      const cardTypes = get(card, ["metadata", "attributes", "cardtype"], []);

      if (Array.isArray(cardTypes) && cardTypes.includes("Creator Youtube")) {
        acc.creatorYoutubeCards.push(card);
      } else {
        acc.normalCards.push(card);
      }

      return acc;
    },
    { normalCards: [], creatorYoutubeCards: [] }
  );

  const creatorYoutubeVideos = creatorYoutubeCards.flatMap((card) =>
    get(card, ["story-elements"], []).filter((el) => el.type === "youtube-video" && el["embed-url"])
  );

  const getYoutubeVideoId = (url = "") => {
    try {
      if (url.includes("embed")) {
        return url.split("/embed/")[1].split("?")[0];
      }
      if (url.includes("watch")) {
        return new URL(url).searchParams.get("v");
      }
    } catch (e) {
      return null;
    }
    return null;
  };

  const getYoutubeThumbnail = (videoId) => `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;

  const socialLinks = {
    facebook: get(storyMetadata, ["creator-facebook-handle"], "").trim(),
    twitter: get(storyMetadata, ["creator-twitter-handle"], "").trim(),
    youtube: get(storyMetadata, ["creator-youtube-handle"], "").trim(),
    instagram: get(storyMetadata, ["creator-instagram-handle"], "").trim(),
  };

  const hasAllSocialLinks = Object.values(socialLinks).some(Boolean);

  // 👉 make a SINGLE stable ad renderer
  const Ad = useCallback(
    (props) => <AdComponent {...props} recommendedStories={recommendedStories} isDesktop={isDesktop} story={story} />,
    [recommendedStories, isDesktop, story?.id]
  );
  const pushInteractionEvent = ({ eventName, eventAction, linkText, callback }) => {
    const pageType = "story-page";
    const { category, subCategory, contentId } = getCategoryAndSubCategory(window.location, pageType, qtConfig, story);

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: eventName,
      event_action: eventAction,
      ...(linkText ? { link_text: linkText } : {}),
      category,
      sub_category: subCategory,
      content_id: contentId,
      template_name: getTemplateName(story?.["story-template"] || pageType),
      platform: window.innerWidth >= 768 ? "desktop" : "mobile",
    });
    if (typeof callback === "function") {
      callback();
    }
  };
  const trackStoryInteraction = (eventAction, callback) => {
    pushInteractionEvent({ eventName: "floatingicon_click", eventAction, callback });
  };
  const trackFaqInteraction = (eventAction, linkText, callback) => {
    pushInteractionEvent({ eventName: "faq_interactions", eventAction, linkText, callback });
  };
  const { summaryPresent, showSummary, setShowSummary, summaryIndex } = useSummaryCheck(story.cards);

  const handleSummaryClick = () => {
    trackStoryInteraction("summary", () => setShowSummary(true));
  };
  const handleFaqClick = () => {
    trackFaqInteraction("floatingicon_click", "faq", () => setIsfaqPopup(true));
  };
  const handleFaqOverlayClose = () => {
    trackFaqInteraction("overlay_close", "close", () => setIsfaqPopup(false));
  };
  const handleFaqInteraction = (eventAction, linkText) => {
    trackFaqInteraction(eventAction, linkText);
  };
  useEffect(() => {
    document.body.style.overflow = showSummary || isfaqPopup ? "hidden" : "unset";
  }, [showSummary, isfaqPopup]);

  return (
    <div
      ref={targetRef}
      className={isCreatorStory ? "container" : ""}
      styleName={isCreatorStory ? "creator-content" : "cards-wrapper"}
    >
      {isStoryBehindPaywall ? (
        <>
          <StoryElementCard
            story={story}
            card={cards[0]}
            key={get(cards[0], ["id"], "")}
            recommendedStories={recommendedStories}
            adComponent={Ad}
          />
          <Paywall magazine={true} />
        </>
      ) : (
        cards.map((card, index) => {
          return (
            <div key={index}>
              <StoryElementCard card={card} story={story} recommendedStories={recommendedStories} adComponent={Ad} />
            </div>
          );
        })
      )}
      {hasAllSocialLinks && (
        <div styleName="social-container">
          <div styleName="inline-video-heading">{storyHeadline}'s Social Presence</div>

          <ul>
            {socialLinks.facebook && (
              <li>
                <a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer">
                  <i styleName="fbicon" /> Facebook
                </a>
              </li>
            )}

            {socialLinks.twitter && (
              <li>
                <a href={socialLinks.twitter} target="_blank" rel="noopener noreferrer">
                  <i styleName="xicon" /> X
                </a>
              </li>
            )}

            {socialLinks.youtube && (
              <li>
                <a href={socialLinks.youtube} target="_blank" rel="noopener noreferrer">
                  <i styleName="youtubeicon" /> YouTube
                </a>
              </li>
            )}

            {socialLinks.instagram && (
              <li>
                <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer">
                  <i styleName="instaicon" /> Instagram
                </a>
              </li>
            )}
          </ul>
        </div>
      )}

      {!isStoryBehindPaywall && creatorYoutubeVideos.length > 0 && (
        <div styleName="creator-video-container">
          <div styleName="creator-video-containerIn">
            <div styleName="inline-video-heading">Popular Content from {storyHeadline}</div>
            <div styleName="creator-video-outer">
              {creatorYoutubeVideos.map((video, index) => {
                const videoId = getYoutubeVideoId(video["embed-url"] || video.url);

                if (!videoId) return null;

                return (
                  <div styleName="creator-video-box" key={video.id || index}>
                    <a href={`https://www.youtube.com/watch?v=${videoId}`} target="_blank" rel="noopener noreferrer">
                      <div styleName="inline-frame-box">
                        <i styleName="youtube-icon" />
                        <img src={getYoutubeThumbnail(videoId)} alt="YouTube video thumbnail" loading="lazy" />
                      </div>
                    </a>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
      {hasFaqData && isfaqPopup ? (
        <>
          {isfaqPopup && <div styleName="blur-bg" onClick={handleFaqOverlayClose} />}
          <div styleName={`faq-popup ${isfaqPopup ? "active" : ""}`}>
            <div styleName="popup-header-logo">
                <i styleName="fortune-faq-logo" />
              <button
                styleName="popup-close-button"
                onClick={() => setIsfaqPopup(false)}
                aria-label="Close FAQ"
              ></button>
            </div>
            <div styleName="faq-popup-inner">
              <FaqCard
                story={story}
                faqData={faqData}
                isfaqPopup={isfaqPopup}
                setIsfaqPopup={setIsfaqPopup}
                onFaqInteraction={handleFaqInteraction}
              />
            </div>
          </div>
        </>
      ) : (
        ""
      )}
      {hasFaqData && (
        <FaqCard
          story={story}
          faqData={faqData}
          isNoscroll={true}
          onFaqVisibilityChange={setIsFaqInViewport}
          onFaqInteraction={handleFaqInteraction}
        />
      )}
      {!isAdfreePage && isStoryBehindPaywall ? null : (
        <>
          {!isWebView && <StoryTags tags={story.tags} template="noBorder" story={story} />}

          <MetypeCommentsWidgetWrapper story={story} />

          <MoreAboutAuthors authors={story.authors} story={story} />

          {taboola && (
            <>
              <div id="taboola-below-article-thumbnails" className="TABOOLA"></div>
              <div
                dangerouslySetInnerHTML={{
                  __html: `
                      <script type="text/javascript">
                        window._taboola = window._taboola || [];
                        _taboola.push({
                          mode: 'alternating-thumbnails-a',
                          container: 'taboola-below-article-thumbnails',
                          placement: 'Below Article Thumbnails',
                          target_type: 'mix'
                        });
                      </script>
                  `,
                }}
              />
            </>
          )}
        </>
      )}
      {isTexteStory && hasFaqData && !isFaqInViewport && (
        <>
          <div styleName={`sticky-left-bar-outer ${isTop ? "active" : ""} ${isAdfreePage ? "adfree-space" : ""}`}>
            <div styleName={isAiOpen ? "sticky-left-bar" : "sticky-left-bar hidden"}>
              {hasFaqData && (
                <button aria-label="Open FAQs" onClick={handleFaqClick} styleName="questionsicon">
                  FAQs
                </button>
              )}
              {summaryPresent && (
                <button aria-label="Open Summary" onClick={handleSummaryClick} styleName="summaryicon">
                  Summarise
                </button>
              )}
            </div>
            <button
              aria-label="Ai-assistant-icon"
              styleName={isAiOpen ? "aiclose-icon active" : "aiclose-icon"}
              onClick={handleAiClick}
            ></button>
          </div>
          {summaryPresent && showSummary && (
            <SummaryCard setShowSummary={setShowSummary} story={story} summaryIndex={summaryIndex} />
          )}
          {showSummary && summaryPresent && <div styleName="blur-bg" />}
        </>
      )}
    </div>
  );
};

RenderCards.propTypes = {
  story: object,
  cards: array,
  recommendedStories: array,
};

const StoryElements = ({ story, cards, recommendedStories, config }) => {
  const qtState = useSelector((state) => state);
  const isAdfreePage = get(qtState, ["qt", "config", "subscriber"]) === "true";
  const taboola = get(config, ["publisher-attributes", "taboola"], true);
  const isWebView = get(qtState, ["qt", "config", "isWebView"]) === true;
  const isCreatorStory = get(story, ["story-template"], "") === "creator";
  const { ref, inView } = useInView({
    triggerOnce: true,
    rootMargin: "300px 0px",
    threshold: 0.01,
  });

  useEffect(() => {
    if (inView && taboola === true && !isCreatorStory && !isWebView && !isAdfreePage) {
      window._taboola = window._taboola || [];
      window._taboola.push({ article: "auto" });

      if (!document.getElementById("tb_loader_script")) {
        const s = document.createElement("script");
        s.async = true;
        s.src = "//cdn.taboola.com/libtrc/jambojar-fortuneindia/loader.js";
        s.id = "tb_loader_script";

        const f = document.getElementsByTagName("script")[0];
        f.parentNode.insertBefore(s, f);
      }

      if (window.performance && typeof window.performance.mark === "function") {
        window.performance.mark("tbl_ic");
      }
    }
  }, [inView, isWebView]);
  return (
    <div id="story-wrapper" styleName="story-elements-wrapper" ref={ref}>
      <RenderCards
        story={story}
        cards={cards}
        recommendedStories={recommendedStories}
        taboola={taboola}
        isCreatorStory={isCreatorStory}
      />
    </div>
  );
};

StoryElements.propTypes = {
  story: object,
  cards: array,
  recommendedStories: array,
};

export default StoryElements;
